import { useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import ListItemLabelWrapper from 'components/list/list-item/ListItemLabelWrapper';
import ListItemLabel from 'components/list/list-item/ListItemLabel';
import ListItemStatus from 'components/list/list-item/ListItemStatus';
import ListItem from 'components/list/list-item/ListItem';
import Button from 'components/button/Button';
import {
  ROUTE_SETTINGS_ENTITY,
  ROUTE_SETTINGS_ORGANISATIONS_ORG
} from 'routes/RouteList';
import { FormattedMessage } from 'react-intl';
import Icon from 'components/icon/Icon';
import { sortEntities } from 'utils/sort';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import Breadcrumb from 'components/breadcrumbs/Breadcrumb';
import List from 'components/list/List';
import ListBody from 'components/list/ListBody';
import messages from 'messages';
import { QUERY_GET_ORGANISATIONS_BY_ID } from 'services/aws/entity-query';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import { EntityTypes } from 'constants.js';
import Loader from 'components/loader/Loader';
import { useQuery } from '@apollo/client';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import AddOrganisationModal from 'containers/pages/settings/organisations/AddOrganisationModal';
import {
  Panel,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleWrapper,
  PanelTools
} from 'components/panel';

const Organisations = () => {
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const { entityId, organisationId } = useParams();
  const [groups, setGroups] = useState([]);
  const [addOrganisationModal, setAddOrganisationModal] = useState(null);
  const { data, loading, error } = useQuery(QUERY_GET_ORGANISATIONS_BY_ID, {
    variables: {
      id: entityId
    }
  });

  useEffect(() => {
    if (data?.getOrganisations) {
      const entities = data.getOrganisations.subEntities;
      const filteredEntities = entities.filter(
        e => e.type === EntityTypes.ORGANISATION
      );
      setGroups(sortEntities(filteredEntities));
    }
  }, [data]);

  return (
    <Panel>
      <PanelHeader borderBottom>
        <Breadcrumbs>
          <Breadcrumb
            onClick={() =>
              push(generatePath(ROUTE_SETTINGS_ENTITY, { entityId }))
            }
          >
            <FormattedMessage {...messages.breadcrumbSettings} />
          </Breadcrumb>
        </Breadcrumbs>
        <PanelTitleWrapper>
          <PanelTitle>
            <FormattedMessage {...messages.organisationsTitle} />
          </PanelTitle>
          <PanelSubtitle>
            <FormattedMessage {...messages.organisationsSubtitle} />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTools>
          {ability.can('create', 'Organisations') && (
            <Button
              rounded
              primary
              onClick={() => setAddOrganisationModal(true)}
            >
              <Icon id="add-white" />
              <FormattedMessage
                {...messages.organisationsAddOrganisationButton}
              />
            </Button>
          )}
        </PanelTools>
      </PanelHeader>
      {loading && <Loader />}
      {error && <RetryPanel />}
      {groups.length > 0 && (
        <List>
          <ListBody>
            {groups.map(org => (
              <OrganisationListItem
                entityId={entityId}
                key={org.id}
                organisation={org}
                push={push}
                active={organisationId === org.id}
              />
            ))}
          </ListBody>
        </List>
      )}
      {addOrganisationModal && (
        <AddOrganisationModal onClose={() => setAddOrganisationModal(false)} />
      )}
    </Panel>
  );
};

export default Organisations;

const OrganisationListItem = ({
  entityId,
  active,
  organisation: { id, name }
}) => {
  const { push } = useHistory();

  return (
    <ListItem
      onClick={() =>
        push(
          generatePath(
            ROUTE_SETTINGS_ORGANISATIONS_ORG.replace(':organisationId', id),
            { entityId }
          )
        )
      }
      active={active}
    >
      <ListItemStatus statusIcon="organisations-white" />
      <ListItemLabelWrapper>
        <ListItemLabel label>{name}</ListItemLabel>
      </ListItemLabelWrapper>
    </ListItem>
  );
};
